import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Segmented, Button, Skeleton } from "antd";
import { DashboardOutlined, ReloadOutlined } from "@ant-design/icons";

import { useApi, ApiNames } from "../../hooks/api";
import { DashboardKpis } from "./DashboardKpis";
import { DashboardFunnel } from "./DashboardFunnel";
import { DashboardOutcomes } from "./DashboardOutcomes";
import { FailedRunsPanel } from "./FailedRunsPanel";
import { SectionError } from "./SectionError";
import { DashboardContainer, HeaderRow, HeaderLeft, HeaderControls, Card, TwoCol } from "./dashboardStyles";
import { useAutoRefetch } from "./useAutoRefetch";
import { BetaTagContainer, BetaTagText } from "../../component/AIChat/Styles";
import { PRIMARY_600 } from "../../constant/colors";

const { Title } = Typography;

const DAY_MS = 24 * 60 * 60 * 1000;
// Range options for the funnel / outcomes / totals (current-state panels ignore
// these). 30 days is the default — a typical reporting window.
const RANGE_DAYS = Object.freeze({ WEEK: 7, MONTH: 30, QUARTER: 90 });

/**
 * Agentic Platform — Dashboard. The numbers behind the agent: a run funnel
 * (how many patients ran, where they dropped off), the outcome mix, the
 * headline KPIs, and a "needs attention now" list of patients whose latest run
 * failed.
 *
 * Two time semantics on one page: the funnel, outcome breakdown, and the first
 * three KPIs are RANGE-scoped (the selected window, default last 30 days); the
 * "Waiting" KPIs and the failed-runs panel are CURRENT-state and ignore the
 * range. The range control refetches the whole payload (the platform recomputes
 * the range-scoped parts; the current-state parts are range-independent there).
 */
const AgenticPlatformDashboard = () => {
  const { t } = useTranslation();

  const [rangeDays, setRangeDays] = useState(RANGE_DAYS.MONTH);
  const [fetchStats, result, isFetching, fetchError] = useApi(ApiNames.AgenticRunStats);

  // `useApi` hands back a fresh `fetchStats` each render — drive the fetch off a
  // ref + stable inputs so it never loops on the function identity (mirrors the
  // Tasks-hub sections). `refetch` re-derives the from/to bounds from the
  // selected range each call, so a range change (new `refetch` identity) and the
  // Refresh button both re-query with the right window.
  const fetchStatsRef = useRef(fetchStats);
  fetchStatsRef.current = fetchStats;
  const refetch = useCallback(() => {
    const to = new Date();
    const from = new Date(to.getTime() - rangeDays * DAY_MS);
    fetchStatsRef.current({ from: from.toISOString(), to: to.toISOString() });
  }, [rangeDays]);

  // Initial load + minute auto-refresh, re-armed when the range changes (new
  // `refetch`). The "right now" tiles + needs-attention panel stay live; polling
  // pauses while the tab is hidden.
  useAutoRefetch(refetch);

  const rangeOptions = [
    { label: t("agentic_dashboard.range.week"), value: RANGE_DAYS.WEEK },
    { label: t("agentic_dashboard.range.month"), value: RANGE_DAYS.MONTH },
    { label: t("agentic_dashboard.range.quarter"), value: RANGE_DAYS.QUARTER }
  ];

  return (
    <DashboardContainer data-testid="agentic-dashboard-page">
      <HeaderRow>
        <HeaderLeft>
          <DashboardOutlined style={{ fontSize: 24, color: PRIMARY_600 }} />
          <Title level={3} style={{ margin: 0 }}>
            {t("agentic_dashboard.page_title")}
          </Title>
          <BetaTagContainer>
            <BetaTagText>BETA</BetaTagText>
          </BetaTagContainer>
        </HeaderLeft>
        <HeaderControls>
          <Segmented
            options={rangeOptions}
            value={rangeDays}
            onChange={setRangeDays}
            data-testid="agentic-dashboard-range"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={refetch}
            loading={isFetching}
            data-testid="agentic-dashboard-refresh"
          >
            {t("agentic_dashboard.refresh")}
          </Button>
        </HeaderControls>
      </HeaderRow>

      {fetchError && !isFetching ? (
        <SectionError onRetry={refetch} testId="agentic-dashboard-error" />
      ) : !result && isFetching ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : (
        <>
          <DashboardKpis stats={result} />
          <TwoCol>
            <DashboardFunnel stats={result} />
            <DashboardOutcomes stats={result} />
          </TwoCol>
          <FailedRunsPanel stats={result} />
        </>
      )}
    </DashboardContainer>
  );
};

export { AgenticPlatformDashboard };
